// Dynamic billing (DYNAMIC, v2 "rules" model) — pure helpers shared by the
// register form (serialize/deserialize) and the rule-builder UI component.
//
// The builder edits a richer UI-state (`U`) that carries per-item operator /
// paren / combine-override metadata the engine config does not. `compileRules`
// turns U into the engine's DynamicRulesConfig v2 (what the backend validates +
// prices with); `deserializeRules` turns a stored config back into U so an
// existing endpoint round-trips. Ported 1:1 from the standalone prototype
// (xapi-backend/tools/dynamic-billing-panel/index.html) so preview == engine.

export const ROOTS = [
  'request.headers',
  'request.query',
  'request.body',
  'response.headers',
  'response.body',
];
export const ROOTLABEL = {
  'request.headers': '请求头',
  'request.query': '请求 query',
  'request.body': '请求体',
  'response.headers': '响应头',
  'response.body': '响应体',
};
export const OPS = ['=', '!=', '>', '>=', '<', '<='];
export const FIELD_TYPES = [
  { v: 'number', l: 'number' },
  { v: 'string', l: 'string' },
  { v: 'map', l: 'map' },
];

const numOr = (v, d) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
// Show small/large numbers as a plain decimal string (avoid 3e-7 scientific form in inputs).
export function toPlainDecimal(v) {
  if (v === '' || v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (!/e/i.test(String(n))) return String(n);
  return n.toLocaleString('en-US', { maximumFractionDigits: 20, useGrouping: false });
}
export const isResp = (s) => typeof s === 'string' && s.startsWith('response.');
export const numFieldsOf = (U) =>
  (U?.fields || []).filter((f) => f.type === 'number' && f.name).map((f) => f.name);
export const mapFieldsOf = (U) =>
  (U?.fields || []).filter((f) => f.type === 'map' && f.name).map((f) => f.name);

// ---- factory defaults (fresh UI-state pieces) ----
export const newField = () => ({
  name: '',
  source: 'response.body',
  path: '',
  type: 'number',
  estMax: '',
  estTyp: '',
  default: 0,
  clampMin: '',
  clampMax: '',
  _open: true,
});
export const newFactor = (kind, extra) =>
  Object.assign(
    { kind: kind || 'const', value: 0, field: '', options: [], onUnmatched: { mode: 'reject', price: 0 }, _open: false },
    extra || {},
  );
export const newItem = (numFieldNames = []) => {
  const fs = numFieldNames.length
    ? [newFactor('number', { field: numFieldNames[0] }), newFactor('const', { value: 0 })]
    : [newFactor('const', { value: 0.01 })];
  return { op: '+', open: false, close: false, factors: fs };
};
export const newRule = (numFieldNames = []) => ({
  isElse: false,
  conds: [{ field: '', op: '=', value: '' }], // 复合条件(AND):多条全部成立才命中
  items: [newItem(numFieldNames)],
  combineOverride: '',
});
export const newCond = () => ({ field: '', op: '=', value: '' });
// A fresh DYNAMIC endpoint: no fields, one flat-rate rule ($0.01). Valid config
// (a when-less rule is the else branch), so saving immediately passes the backend.
export const EMPTY_DYNAMIC_UI = () => ({
  holdPolicy: 'max',
  minCost: '',
  maxCost: '',
  fields: [],
  rules: [newRule([])],
});

// ---- combine expression (item operators + parens → engine string) ----
export function builtExpr(items) {
  const p = [];
  (items || []).forEach((it, i) => {
    if (i > 0) p.push(it.op || '+');
    if (it.open) p.push('(');
    p.push('o' + (i + 1));
    if (it.close) p.push(')');
  });
  return p.join(' ') || 'o1';
}
// engine combine: override wins; all-plain (no op/paren) ⇒ '' (engine sums); else built expr.
export function effectiveCombineEngine(r) {
  if (r.combineOverride && r.combineOverride.trim()) return r.combineOverride.trim();
  const plain = (r.items || []).every((it) => (it.op || '+') === '+' && !it.open && !it.close);
  return plain ? '' : builtExpr(r.items);
}
// the value shown in the combine box: override wins, else it live-follows the item ops.
export function boxVal(r) {
  return r.combineOverride && r.combineOverride.trim() ? r.combineOverride : builtExpr(r.items);
}
export function ruleLabel(r) {
  const active = (r.conds || []).filter((c) => c.field);
  return r.isElse || !active.length ? '否则' : active.map((c) => `${c.field} ${c.op} ${c.value}`).join(' 且 ');
}

// ---- compile: UI-state U → engine DynamicRulesConfig v2 ----
function compileFactor(f) {
  if (f.kind === 'number') return { kind: 'number', field: f.field };
  if (f.kind === 'map')
    return {
      kind: 'map',
      field: f.field,
      options: (f.options || []).map((o) => ({ key: o.key, price: numOr(o.price, 0) })),
      onUnmatched:
        f.onUnmatched && f.onUnmatched.mode === 'default'
          ? { mode: 'default', price: numOr(f.onUnmatched.price, 0) }
          : { mode: 'reject' },
    };
  return { kind: 'const', value: numOr(f.value, 0) };
}
const compileItem = (it) => ({ factors: (it.factors || []).map(compileFactor) });

export function compileRules(U) {
  if (!U || typeof U !== 'object') return { version: 2, fields: [], rules: [] };
  const typeOf = {};
  (U.fields || []).forEach((f) => {
    typeOf[f.name] = f.type;
  });
  const fields = (U.fields || []).map((f) => {
    const o = { name: f.name, source: f.source, path: f.path, type: f.type };
    if (f.type === 'number') {
      if (f.estMax !== '' && f.estMax != null) o.estimatedMax = Number(f.estMax);
      if (f.estTyp !== '' && f.estTyp != null) o.estimatedTypical = Number(f.estTyp);
      if (f.default !== '' && f.default != null) o.default = Number(f.default);
      const c = {};
      if (f.clampMin !== '' && f.clampMin != null) c.min = Number(f.clampMin);
      if (f.clampMax !== '' && f.clampMax != null) c.max = Number(f.clampMax);
      if (Object.keys(c).length) o.clamp = c;
    }
    return o;
  });
  const rules = (U.rules || []).map((r) => {
    const then = { operands: (r.items || []).map(compileItem) };
    const comb = effectiveCombineEngine(r);
    if (comb) then.combine = comb;
    const rule = { label: ruleLabel(r), then };
    const active = (r.conds || []).filter((c) => c.field);
    if (!r.isElse && active.length) {
      const conds = active.map((c) => {
        const t = typeOf[c.field];
        return { field: c.field, op: c.op, value: t === 'number' ? Number(c.value) : c.value };
      });
      rule.when = conds.length === 1 ? conds[0] : conds; // 单条 → Condition,多条 → 数组(AND)
    }
    return rule;
  });
  const cfg = { version: 2, fields, rules };
  if (U.holdPolicy) cfg.holdPolicy = U.holdPolicy;
  if (U.minCost !== '' && U.minCost != null) cfg.minCost = Number(U.minCost);
  if (U.maxCost !== '' && U.maxCost != null) cfg.maxCost = Number(U.maxCost);
  return cfg;
}

// ---- deserialize: engine config → UI-state U ----
function deFactor(f) {
  return {
    kind: f.kind,
    value: f.kind === 'const' ? toPlainDecimal(f.value) : 0,
    field: f.field || '',
    options: (f.options || []).map((o) => ({ key: o.key, price: toPlainDecimal(o.price) })),
    onUnmatched: { mode: (f.onUnmatched && f.onUnmatched.mode) || 'reject', price: toPlainDecimal((f.onUnmatched && f.onUnmatched.price) ?? 0) },
    _open: false,
  };
}
const deItem = (op) => ({ op: '+', open: false, close: false, factors: (op.factors || []).map(deFactor) });

// If a combine string is a plain flat `o1 op o2 op … op oN` over all operands in order
// (operators + - * /, no parens, no bare constants), return the per-unit operators so the
// operator selects render correctly and no override box is needed. Otherwise null (kept as
// a manual combineOverride — e.g. parens like (o1+o2)*1.1). Standard precedence applies and
// builtExpr reproduces the exact string, so linearising is lossless.
function linearOps(combine, n) {
  const c = (combine || '').trim();
  if (!c || n < 1 || /[()]/.test(c)) return null;
  const toks = c.match(/o\d+|[+\-*/]/g);
  if (!toks || toks.length !== 2 * n - 1) return null;
  const ops = ['+'];
  for (let i = 0; i < n; i++) {
    if (toks[2 * i] !== 'o' + (i + 1)) return null;
    if (i > 0) {
      const opTok = toks[2 * i - 1];
      if (!'+-*/'.includes(opTok)) return null;
      ops[i] = opTok;
    }
  }
  return ops;
}

export function deserializeRules(cfg) {
  if (!cfg || typeof cfg !== 'object') return EMPTY_DYNAMIC_UI();
  // Already UI-state (has rules with `items`) — pass through (defensive round-trip).
  if (Array.isArray(cfg.rules) && cfg.rules.some((r) => Array.isArray(r.items))) return cfg;
  const U = {
    holdPolicy: cfg.holdPolicy || 'max',
    minCost: cfg.minCost ?? '',
    maxCost: cfg.maxCost ?? '',
    fields: [],
    rules: [],
  };
  U.fields = (cfg.fields || []).map((f) => ({
    name: f.name,
    source: f.source,
    path: f.path,
    type: f.type,
    estMax: f.estimatedMax ?? '',
    estTyp: f.estimatedTypical ?? '',
    default: f.default ?? '',
    clampMin: (f.clamp && f.clamp.min) ?? '',
    clampMax: (f.clamp && f.clamp.max) ?? '',
    _open: false,
  }));
  U.rules = (cfg.rules || []).map((r) => {
    const items = ((r.then && r.then.operands) || []).map(deItem);
    let combineOverride = (r.then && r.then.combine) || '';
    const ops = linearOps(combineOverride, items.length);
    if (ops) {
      items.forEach((it, i) => { if (i > 0) it.op = ops[i]; });
      combineOverride = '';
    }
    const whenRaw = r.when ? (Array.isArray(r.when) ? r.when : [r.when]) : [];
    const conds = whenRaw.map((c) => ({ field: c.field, op: c.op, value: String(c.value) }));
    return {
      isElse: !whenRaw.length,
      conds: conds.length ? conds : [{ field: '', op: '=', value: '' }],
      items,
      combineOverride,
    };
  });
  if (!U.rules.length) U.rules = [newRule([])];
  return U;
}

// ---- formula-string preview (pure, no engine) ----
const fmt = (n) => {
  if (typeof n !== 'number' || !isFinite(n)) return String(n);
  return n.toFixed(6).replace(/\.?0+$/, (m) => (m.includes('.') ? '' : m));
};
const factorStr = (f) =>
  f.kind === 'const'
    ? String(f.value ?? 0)
    : f.kind === 'map'
      ? `${f.field || '?'}(查表)`
      : f.field || '?';
const itemStr = (it) => (it.factors || []).map(factorStr).join(' × ') || '?';
function combineDisplay(r) {
  if (r.combineOverride && r.combineOverride.trim())
    return r.combineOverride.trim().replace(/\*/g, '×').replace(/\//g, '÷');
  const p = [];
  (r.items || []).forEach((it, i) => {
    const op = it.op || '+';
    const sym = op === '*' ? '×' : op === '/' ? '÷' : op === '-' ? '−' : '+';
    if (i > 0) p.push(sym);
    if (it.open) p.push('(');
    p.push('o' + (i + 1));
    if (it.close) p.push(')');
  });
  return p.join(' ') || '0';
}
export function previewStr(r) {
  let s = combineDisplay(r);
  (r.items || []).forEach((it, i) => {
    s = s.replace(new RegExp('\\bo' + (i + 1) + '\\b', 'g'), '[' + itemStr(it) + ']');
  });
  return s;
}

// ---- numeric preview (illustrative; pure JS, NOT the authoritative engine) ----
// Minimal TOTAL arithmetic evaluator for the combine string: numbers, o-identifiers
// (from scope), + - * / and parens. No eval/new Function. Returns NaN on any error.
export function evalArith(expr, scope) {
  if (!expr || !String(expr).trim()) return NaN;
  const toks = String(expr).match(/\d+\.?\d*|[a-zA-Z_]\w*|[+\-*/()]/g);
  if (!toks) return NaN;
  const prec = { '+': 1, '-': 1, '*': 2, '/': 2 };
  const out = [];
  const ops = [];
  for (const t of toks) {
    if (/^\d/.test(t)) out.push(Number(t));
    else if (/^[a-zA-Z_]/.test(t)) {
      const v = scope[t];
      if (!Number.isFinite(v)) return NaN;
      out.push(v);
    } else if (t === '(') ops.push(t);
    else if (t === ')') {
      while (ops.length && ops[ops.length - 1] !== '(') out.push(ops.pop());
      if (!ops.length) return NaN;
      ops.pop();
    } else {
      while (ops.length && ops[ops.length - 1] !== '(' && prec[ops[ops.length - 1]] >= prec[t]) out.push(ops.pop());
      ops.push(t);
    }
  }
  while (ops.length) {
    const o = ops.pop();
    if (o === '(') return NaN;
    out.push(o);
  }
  const st = [];
  for (const x of out) {
    if (typeof x === 'number') st.push(x);
    else {
      const b = st.pop();
      const a = st.pop();
      if (a === undefined || b === undefined) return NaN;
      st.push(x === '+' ? a + b : x === '-' ? a - b : x === '*' ? a * b : b === 0 ? NaN : a / b);
    }
  }
  return st.length === 1 ? st[0] : NaN;
}

const round6 = (x) =>
  !Number.isFinite(x) ? x : (Math.round(Math.abs(x) * 1e6 + 1e-9) / 1e6) * (x < 0 ? -1 : 1);

// Sample value for a number field (typical → max → default → 1000) — drives the preview.
function sampleNumber(f) {
  const pick = [f.estTyp, f.estMax, f.default].find((v) => v !== '' && v != null && Number.isFinite(Number(v)));
  const v = pick != null ? Number(pick) : 1000;
  return Number.isFinite(v) && v > 0 ? v : 1000;
}

// Illustrative per-rule numeric preview: plug sample field values into the formula,
// show the arithmetic and the rounded $ result. Mirrors engine semantics (product of
// factors, combine, clamp, round6) but is a frontend estimate — the backend validator
// + the live engine remain authoritative.
export function numericPreview(rule, fields, opts = {}) {
  const numSample = {};
  (fields || []).forEach((f) => {
    if (f.type === 'number' && f.name) numSample[f.name] = sampleNumber(f);
  });
  const operandVals = [];
  const operandStrs = [];
  (rule.items || []).forEach((it) => {
    let v = 1;
    const parts = [];
    (it.factors || []).forEach((f) => {
      if (f.kind === 'const') {
        const c = Number(f.value) || 0;
        v *= c;
        parts.push(fmt(c));
      } else if (f.kind === 'number') {
        const s = numSample[f.field] ?? 0;
        v *= s;
        parts.push(String(s));
      } else if (f.kind === 'map') {
        const opt = (f.options || [])[0];
        const price = opt ? Number(opt.price) || 0 : f.onUnmatched && f.onUnmatched.mode === 'default' ? Number(f.onUnmatched.price) || 0 : 0;
        v *= price;
        parts.push(fmt(price) + (opt ? `(${opt.key})` : ''));
      }
    });
    operandVals.push(v);
    operandStrs.push(parts.join('×') || '0');
  });
  const comb = effectiveCombineEngine(rule);
  let result;
  if (!comb) result = operandVals.reduce((a, b) => a + b, 0);
  else {
    const scope = {};
    operandVals.forEach((v, i) => {
      scope['o' + (i + 1)] = v;
    });
    result = evalArith(comb, scope);
  }
  const ok = Number.isFinite(result);
  if (ok) {
    const lo = opts.minCost !== '' && opts.minCost != null ? Number(opts.minCost) : 0;
    const hi = opts.maxCost !== '' && opts.maxCost != null ? Number(opts.maxCost) : Infinity;
    result = round6(Math.min(Math.max(result, lo), hi));
  }
  const usedNums = [
    ...new Set((rule.items || []).flatMap((it) => (it.factors || []).filter((f) => f.kind === 'number').map((f) => f.field))),
  ].filter(Boolean);
  const summary = usedNums.map((n) => `${n}=${numSample[n] ?? 0}`).join(' / ');
  const exprStr = comb
    ? comb.replace(/o(\d+)/g, (_, i) => operandStrs[i - 1] ?? '?').replace(/\*/g, '×').replace(/\//g, '÷')
    : operandStrs.join(' + ');
  return { summary, exprStr, result, ok };
}
